import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { NoteIcon, PlusIcon, FileTextIcon } from '@phosphor-icons/react';
import type {
  MemoryBankNoteMetadata,
  CreateNoteInput,
  NoteUsage,
  MemoryBankNote,
  UpdateNoteInput,
  MemoryBankSupportedEntityTypes,
} from '@erp/rpc';
import {
  useCreateMemoryBankNote,
  useUpdateMemoryBankNote,
} from '@/api/core/ai/memory-bank/mutations';
import { useListNoteUsages } from '@/api/core/ai/memory-bank/queries';
import { NoteEditForm } from './NoteEditForm';
import { NotesUsedDisplay } from './NotesUsedDisplay';
import { useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import { SystemEntity } from '@erp/shared-types';
import CustomerPill from '@/components/customer/CustomerPill';
import SupplierPill from '@/components/company/SupplierPill';

export interface MemoryBankNotesProps {
  staticMetadata?: Partial<MemoryBankNoteMetadata>;
  dynamicMetadata?: Partial<MemoryBankNoteMetadata>;
  /**
   * Note usages to display. If entityId and entityType are provided, this will be fetched automatically
   */
  notesUsed?: NoteUsage[];
  /**
   * Max number of notes to show in the compact view
   */
  maxVisible?: number;
  /**
   * Size of the icon
   */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * Called when notes change (create, update, delete)
   */
  onNotesChange?: () => void;
  /**
   * Entity ID to link notes to - when provided with entityType, will fetch usages automatically
   */
  entityId: string;
  /**
   * Entity type to link notes to - when provided with entityId, will fetch usages automatically
   */
  entityType: MemoryBankSupportedEntityTypes;
  /**
   * Optional tooltip text to explain what this feature does
   */
  tooltipText?: string;
}

// Simplified tooltip content
function useNotesTooltipContent(
  notesUsed: NoteUsage[] | undefined,
  isLoading: boolean,
) {
  if (isLoading) {
    return 'Loading notes...';
  }

  if (!notesUsed || notesUsed.length === 0) {
    return 'Add AI workflow notes';
  }

  const count = notesUsed.length;
  return `${count} note${count !== 1 ? 's' : ''}`;
}

// Icon size mapping
const iconSizes = {
  sm: 'h-5 w-5',
  md: 'h-6 w-6',
  lg: 'h-7 w-7',
};

// Badge size mapping
const badgeSizes = {
  sm: 'h-3.5 w-3.5 text-[9px]',
  md: 'h-4 w-4 text-[10px]',
  lg: 'h-5 w-5 text-xs',
};

// Edit mode state type
type EditMode =
  | { mode: 'view' }
  | { mode: 'create' }
  | { mode: 'edit'; note: MemoryBankNote };

// Get friendly entity type name
function getEntityTypeName(entityType: MemoryBankSupportedEntityTypes): string {
  const entityMap: Record<SystemEntity, string> = {
    [SystemEntity.AR_CUSTOMER]: 'customer',
    [SystemEntity.AR_STAGED_SALES_ORDER]: 'sales order',
    [SystemEntity.AP_INVOICE]: 'invoice',
    [SystemEntity.AP_SUPPLIER]: 'supplier',
    [SystemEntity.CORE_DOCUMENT]: 'document',
    [SystemEntity.WORKFLOW_RUN]: 'workflow run',
    [SystemEntity.STEP_RUN]: 'step run',
    [SystemEntity.INBOUND_EMAIL]: 'email',
    [SystemEntity.OUTBOUND_EMAIL]: 'email',
    [SystemEntity.CORE_MEMORY_BANK_NOTE]: 'note',
    [SystemEntity.CORE_MEMORY_BANK_NOTE_USAGE]: 'note usage',
    [SystemEntity.ORGANIZATION]: 'organization',
    [SystemEntity.CORE_DOCUMENT_OCR_RESULT]: 'OCR result',
    [SystemEntity.AP_INVOICE_LINE_ITEM]: 'invoice line item',
    [SystemEntity.INTERNAL_PURCHASE_ORDER]: 'purchase order',
    [SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM]: 'PO line item',
    [SystemEntity.PAYMENT]: 'payment',
    [SystemEntity.FINANCIAL_TRANSACTION]: 'transaction',
    [SystemEntity.CHARGE]: 'charge',
    [SystemEntity.WORKFLOW_DEFINITION]: 'workflow',
    [SystemEntity.AR_SALES_ORDER]: 'sales order',
    [SystemEntity.AR_SALES_ORDER_LINE_ITEM]: 'sales order line item',
    [SystemEntity.SCM_SHIPMENT]: 'shipment',
    [SystemEntity.SHIPPING_DOCUMENT]: 'shipping document',
    [SystemEntity.SHIPPING_DOCUMENT_LINE_ITEM]: 'shipping document line item',
  };

  return entityMap[entityType as SystemEntity] || 'item';
}

// Component to render entity pills based on type
function EntityPill({
  entityType,
  entityId,
}: {
  entityType: MemoryBankSupportedEntityTypes;
  entityId: string;
}) {
  switch (entityType as SystemEntity) {
    case SystemEntity.AR_CUSTOMER:
      return <CustomerPill customerId={entityId} />;
    case SystemEntity.AP_SUPPLIER:
      return <SupplierPill supplierId={entityId} />;
    // Add more entity types as needed
    default:
      return null;
  }
}

// Empty state component
function EmptyState({
  entityType,
  onAddNote,
}: {
  entityType: MemoryBankSupportedEntityTypes;
  onAddNote: () => void;
}) {
  const entityName = getEntityTypeName(entityType);

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="mb-3 p-3 rounded-full bg-gray-100">
        <FileTextIcon className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-medium text-base mb-1">No notes yet</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-xs">
        Add notes to guide AI workflows for this {entityName}. Notes help
        provide context and improve automation accuracy.
      </p>
      <Button onClick={onAddNote} size="sm">
        <PlusIcon className="h-4 w-4 mr-1" />
        Add your first note
      </Button>
    </div>
  );
}

export function MemoryBankNotes({
  staticMetadata = {},
  dynamicMetadata = {},
  notesUsed: providedNotesUsed,
  className,
  onNotesChange,
  entityId,
  entityType,
  tooltipText,
  size = 'md',
}: MemoryBankNotesProps) {
  // Unified edit mode state
  const [editMode, setEditMode] = useState<EditMode>({ mode: 'view' });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showFirstTimeAnimation, setShowFirstTimeAnimation] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { showToast } = useToast();

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch note usages if entityId and entityType are provided
  const { data: fetchedUsagesData, isLoading: isLoadingUsages } =
    useListNoteUsages(entityId && entityType ? { entityId, entityType } : null);

  // Use provided notesUsed or fetched usages
  const notesUsed = providedNotesUsed ?? fetchedUsagesData?.usages ?? [];

  const invalidateListQuery = useInvalidateRpcQueryFn(
    (orpc) => [
      orpc.memoryBank.list.key(),
      orpc.memoryBank.batchGet.key({
        input: { ids: notesUsed.map((usage) => usage.noteId) },
      }),
      ...(entityId && entityType
        ? [
            orpc.memoryBank.listUsages.key({
              input: { entityId, entityType },
            }),
          ]
        : []),
    ],
    [notesUsed, entityId, entityType],
  );

  // Check if this is the user's first time seeing notes for this entity
  useEffect(() => {
    const storageKey = `memorybank-seen-${entityType}`;
    const hasSeen = localStorage.getItem(storageKey);
    if (!hasSeen && notesUsed.length === 0) {
      setShowFirstTimeAnimation(true);
    }
  }, [entityType, notesUsed.length]);

  // Mark as seen when user interacts
  const markAsSeen = () => {
    const storageKey = `memorybank-seen-${entityType}`;
    localStorage.setItem(storageKey, 'true');
    setShowFirstTimeAnimation(false);
  };

  const defaultTooltipContent = useNotesTooltipContent(
    notesUsed,
    isLoadingUsages,
  );
  const notesTooltipContent = tooltipText || defaultTooltipContent;

  // Merge metadata and filter out null/undefined values
  const mergedMetadata = useMemo(
    () =>
      Object.fromEntries(
        Object.entries({
          ...staticMetadata,
          ...dynamicMetadata,
        }).filter(([, value]) => value !== undefined && value !== null),
      ),
    [staticMetadata, dynamicMetadata],
  );

  const { mutateAsync: createNote, isPending: isCreatingNote } =
    useCreateMemoryBankNote();
  const { mutateAsync: updateNote, isPending: isUpdatingNote } =
    useUpdateMemoryBankNote();

  const handleCreateOrUpdateNote = async (
    noteContent: string,
    filteredMetadata: Partial<MemoryBankNoteMetadata>,
  ) => {
    try {
      if (editMode.mode === 'edit') {
        const input: UpdateNoteInput = {
          note: noteContent,
          metadata: filteredMetadata as MemoryBankNoteMetadata,
        };

        await updateNote({ id: editMode.note.id, data: input });
        showToast({
          message: 'Note updated successfully',
          color: 'success',
          icon: 'check',
        });
      } else {
        const input: CreateNoteInput = {
          note: noteContent,
          metadata: filteredMetadata as MemoryBankNoteMetadata,
          entityId,
          entityType,
        };

        await createNote(input);
        showToast({
          message: 'Note created successfully',
          color: 'success',
          icon: 'check',
        });
      }

      // Close the editor and trigger refetch on success
      setEditMode({ mode: 'view' });
      handleNoteChange();
    } catch (error) {
      showToast({
        message: 'Failed to save note',
        color: 'error',
        icon: 'error',
      });
      throw error;
    }
  };

  const handleCancel = () => {
    setEditMode({ mode: 'view' });
  };

  const handleNoteChange = () => {
    invalidateListQuery();
    onNotesChange?.();
  };

  const handleOpenChange = (open: boolean) => {
    setIsPopoverOpen(open);
    if (open) {
      markAsSeen();
    }
    // Reset to view mode when closing popover
    if (!open) {
      setEditMode({ mode: 'view' });
    }
  };

  const handleStartEdit = (mode: EditMode) => {
    setEditMode(mode);
    // On mobile, close popover when entering edit mode (Dialog will open)
    if (isMobile && mode.mode !== 'view') {
      setIsPopoverOpen(false);
    }
  };

  const hasNotes = notesUsed.length > 0;
  const isPending = isCreatingNote || isUpdatingNote;

  return (
    <>
      <div className={cn('relative inline-flex', className)}>
        <Popover open={isPopoverOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'relative inline-flex items-center justify-center',
                'transition-all duration-200',
                'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 rounded',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                showFirstTimeAnimation && 'animate-pulse',
              )}
              disabled={isLoadingUsages}
              aria-label={`Memory bank notes, ${notesUsed.length} notes`}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative">
                    <NoteIcon
                      className={cn(
                        iconSizes[size],
                        hasNotes
                          ? 'fill-gray-200 text-gray-600'
                          : 'text-gray-400',
                        'transition-colors duration-200',
                      )}
                    />
                    {isLoadingUsages && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                      </div>
                    )}
                    {!isLoadingUsages && hasNotes && (
                      <Badge
                        variant="secondary"
                        className={cn(
                          'absolute -top-1 -right-1 flex items-center justify-center',
                          'bg-gray-600 text-white hover:bg-gray-600',
                          badgeSizes[size],
                          'p-0 min-w-0 border-2 border-white',
                        )}
                      >
                        {notesUsed.length > 9 ? '9+' : notesUsed.length}
                      </Badge>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{notesTooltipContent}</p>
                </TooltipContent>
              </Tooltip>
            </button>
          </PopoverTrigger>

          <PopoverContent className="w-[500px] p-0" align="start">
            {/* Header */}
            <div className="border-b p-3">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {editMode.mode === 'view' ? (
                    <>
                      <h3 className="font-semibold text-sm">
                        Memory Bank Notes
                      </h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <p className="text-xs text-muted-foreground">
                          Notes for
                        </p>
                        {entityId && entityType && (
                          <EntityPill
                            entityType={entityType}
                            entityId={entityId}
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="font-semibold text-sm">
                        {editMode.mode === 'create' ? 'New Note' : 'Edit Note'}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        Notes {' > '}{' '}
                        {editMode.mode === 'create' ? 'New' : 'Edit'}
                      </p>
                    </>
                  )}
                </div>
                {editMode.mode === 'view' && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleStartEdit({ mode: 'create' })}
                    disabled={isPending}
                    className="flex-shrink-0"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add note
                  </Button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="max-h-[70vh] overflow-y-auto">
              {isLoadingUsages ? (
                <div className="p-6 space-y-3">
                  <Skeleton className="h-4 w-full bg-gray-200" />
                  <Skeleton className="h-4 w-3/4 bg-gray-200" />
                  <Skeleton className="h-20 w-full bg-gray-200" />
                </div>
              ) : !isMobile &&
                (editMode.mode === 'create' || editMode.mode === 'edit') ? (
                <div className="p-3">
                  <NoteEditForm
                    initialValue={
                      editMode.mode === 'edit' ? editMode.note.note : undefined
                    }
                    metadata={
                      editMode.mode === 'edit'
                        ? editMode.note.metadata
                        : mergedMetadata
                    }
                    onSave={handleCreateOrUpdateNote}
                    onCancel={handleCancel}
                    isSubmitting={isPending}
                    placeholder=""
                    entityType={entityType}
                    entityId={entityId}
                  />
                </div>
              ) : !hasNotes ? (
                <EmptyState
                  entityType={entityType}
                  onAddNote={() => handleStartEdit({ mode: 'create' })}
                />
              ) : (
                <div className="p-3">
                  <NotesUsedDisplay
                    notesUsed={notesUsed}
                    onEditNote={(note) =>
                      handleStartEdit({ mode: 'edit', note })
                    }
                    metadata={mergedMetadata}
                    entityType={entityType}
                    entityId={entityId}
                  />
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Edit Dialog for mobile */}
      {isMobile && (
        <Dialog
          open={editMode.mode !== 'view'}
          onOpenChange={(open) => {
            if (!open) {
              setEditMode({ mode: 'view' });
            }
          }}
        >
          <DialogContent className="max-w-[95vw]">
            <DialogHeader>
              <DialogTitle>
                {editMode.mode === 'create' ? 'New Note' : 'Edit Note'}
              </DialogTitle>
              <DialogDescription>
                Add notes to guide AI workflows for this{' '}
                {getEntityTypeName(entityType)}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {editMode.mode !== 'view' && (
                <NoteEditForm
                  initialValue={
                    editMode.mode === 'edit' ? editMode.note.note : undefined
                  }
                  metadata={
                    editMode.mode === 'edit'
                      ? editMode.note.metadata
                      : mergedMetadata
                  }
                  onSave={handleCreateOrUpdateNote}
                  onCancel={handleCancel}
                  isSubmitting={isPending}
                  placeholder=""
                  entityType={entityType}
                  entityId={entityId}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
